import type { FC } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FlexRowWrap } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsRatingFilter/styles";
import {
    getNumberOfRatingChipText,
    groupNumbers,
} from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsRatingFilter/utils";
import type FindAppsRatingFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/RatingFilter";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

export const AppsRatingFilterResult: FC<IFilterViewBaseProps<FindAppsRatingFilter>> = ({ onFilterUpdate, value }) => {
    if (!value || (!value.score && !value.numberOfRatings)) {
        return null;
    }

    const isNumberOfRatingsSelected =
        !is.undefined(value.numberOfRatings?.from) || !is.undefined(value.numberOfRatings?.to);
    const isScoreSelected = Boolean(value.score?.length);

    const handleNumberOfRatingsRemove = () => {
        onFilterUpdate(isScoreSelected ? { score: value.score } : undefined);
    };

    const handleScoreRemove = (valuesToRemove: readonly number[]) => {
        const updatedScore = value.score?.filter((v) => !valuesToRemove.includes(v));
        const updatedValue = {
            ...(isNumberOfRatingsSelected ? { numberOfRatings: value.numberOfRatings } : {}),
            ...(updatedScore?.length ? { score: updatedScore } : {}),
        };

        onFilterUpdate(Object.keys(updatedValue).length ? updatedValue : undefined);
    };

    const getScoreChips = () => {
        const formatLabel = (first: number, last: number) => `${first} - ${last}`;

        return groupNumbers(value.score ?? [], formatLabel).map(({ values, label }) => (
            <FiltersChip
                text={`${translate("si.find_apps.filters.rating.select_rating.chip.label")}: ${label}`}
                onClose={() => handleScoreRemove(values)}
                key={label}
            />
        ));
    };

    if (!isScoreSelected && !isNumberOfRatingsSelected) {
        return null;
    }

    return (
        <FlexRowWrap>
            {isScoreSelected ? getScoreChips() : null}
            {isNumberOfRatingsSelected ? (
                <FiltersChip
                    text={getNumberOfRatingChipText(value.numberOfRatings)}
                    onClose={handleNumberOfRatingsRemove}
                />
            ) : null}
        </FlexRowWrap>
    );
};
